import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import {
  BriefcaseIcon,
  ClockIcon,
  CubeIcon,
  PencilIcon,
} from "@heroicons/react/outline";
import SlideOver from "../../Modal/SlideOver";
import ExpandableDescription from "../ExpandableDescription";
import { currencyFormat } from "../../../../Utils";
import { TAX_BASE_TYPES } from "../../../../Constants/products";
import Button from "../../Button";

const TYPE_STYLES = {
  GOODS: {
    badge: "bg-blue-100 text-blue-800",
    icon: CubeIcon,
    label: "products.types.goods",
  },
  SERVICES: {
    badge: "bg-purple-100 text-purple-800",
    icon: BriefcaseIcon,
    label: "products.types.services",
  },
};

const getTaxBaseLabel = (taxBaseType) => {
  const found = TAX_BASE_TYPES.find((t) => t.value === taxBaseType);
  return found ? found.label : taxBaseType;
};

const getTaxBaseDescription = (product, t) => {
  if (!product.taxBaseType) return null;

  switch (product.taxBaseType) {
    case "DECIMAL":
      return product.taxBaseRate != null
        ? currencyFormat(product.taxBaseRate)
        : "-";
    case "PERCENTAGE":
      return product.taxBasePercentage != null
        ? `${product.taxBasePercentage}%`
        : "-";
    case "FRACTION":
      return product.taxBaseFractionNumerator != null &&
        product.taxBaseFractionDenumerator != null
        ? `${product.taxBaseFractionNumerator} / ${product.taxBaseFractionDenumerator}`
        : "-";
    default:
      return "-";
  }
};

const ProductDetailModal = ({
  open,
  onClose,
  onEdit,
  onSetStock,
  onWriteDown,
  product,
}) => {
  const { t, i18n } = useTranslation();

  if (!product) return null;

  const typeStyle = TYPE_STYLES[product.type] || TYPE_STYLES.GOODS;
  const TypeIcon = typeStyle.icon;
  const isGoods = product.type === "GOODS";
  const hasTaxBase = Boolean(product.taxBaseType);
  const taxBaseDescription = getTaxBaseDescription(product, t);

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={t("products.detail.title")}
      actionButton={
        onEdit && (
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            onClick={() => onEdit(product)}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
        )
      }
    >
      <div className="space-y-6 mb-6">
        {/* Header Summary */}
        <div className="bg-white p-4 shadow sm:rounded-lg sm:p-6 border border-gray-100">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
              <TypeIcon className="h-6 w-6 text-gray-500" />
            </div>
            <div className="flex flex-1 items-start space-x-3">
              <div>
                <ExpandableDescription
                  text={product.category?.name || t("common.uncategorized")}
                  maxLength={30}
                  textClassName="text-sm font-medium text-gray-500"
                />
                <ExpandableDescription
                  text={product.name}
                  maxLength={50}
                  textClassName="text-sm font-semibold text-gray-900"
                />
              </div>
            </div>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${typeStyle.badge}`}
            >
              {t(typeStyle.label)}
            </span>
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 mt-4 pt-4 border-t border-gray-200">
            {product.reference && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">
                  {t("products.fields.reference")}
                </dt>
                <dd className="mt-1">
                  <ExpandableDescription
                    text={product.reference}
                    textClassName="text-sm text-gray-900"
                    maxLength={60}
                  />
                </dd>
              </div>
            )}

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("products.fields.price")}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">
                {currencyFormat(product.price)}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("products.fields.cost")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {product.cost != null ? currencyFormat(product.cost) : "-"}
              </dd>
            </div>
          </dl>
        </div>

        {/* Tax Base Section */}
        {hasTaxBase && (
          <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
            <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                {t("products.fields.taxBase")}
              </h4>
            </div>
            <dl className="px-4 py-4 sm:px-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("products.fields.taxBaseType")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {t(getTaxBaseLabel(product.taxBaseType))}
                </dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("products.fields.taxBaseValue")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {taxBaseDescription}
                </dd>
              </div>
            </dl>
          </div>
        )}

        {/* Stock Section – GOODS only */}
        {isGoods && (
          <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
            <div className="px-4 py-4 sm:px-6 border-b border-gray-100 flex justify-between items-center">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                {t("products.fields.stock")}
              </h4>
              <Link
                to={`/v2/accounting/products/stock-history/${product.id}`}
                className="flex items-center text-xs text-blue-600 hover:text-blue-500 font-medium"
                onClick={onClose}
              >
                <ClockIcon className="h-4 w-4 mr-1" />
                {t("products.actions.viewHistory")}
              </Link>
            </div>
            <dl className="px-4 py-4 sm:px-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("products.fields.currentStock")}
                </dt>
                <dd className="mt-1 text-sm font-semibold text-gray-900">
                  {product.currentStock != null ? (
                    <span
                      className={
                        product.currentStock <= 0
                          ? "text-red-600"
                          : "text-gray-900"
                      }
                    >
                      {product.currentStock}
                    </span>
                  ) : (
                    <span className="text-gray-400">
                      {t("products.fields.notSet")}
                    </span>
                  )}
                </dd>
              </div>

              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("products.fields.averageCost")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {product.averageCost != null
                    ? currencyFormat(product.averageCost)
                    : product.cost != null
                      ? currencyFormat(product.cost)
                      : "-"}
                </dd>
              </div>

              {product.stockLastUpdatedAt && (
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("products.fields.stockLastUpdated")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {dayjs(product.stockLastUpdatedAt)
                      .locale(i18n.language)
                      .format("DD MMM YYYY")}
                  </dd>
                </div>
              )}
            </dl>

            {/* Stock action buttons */}
            {(onSetStock || onWriteDown) && (
              <div className="px-4 py-3 sm:px-6 bg-gray-50 border-t border-gray-100 flex gap-3">
                {onSetStock && (
                  <Button
                    type="button"
                    onClick={() => onSetStock(product)}
                    className="flex-1"
                    variant="secondary"
                    color="blue"
                  >
                    {t("products.actions.setStock")}
                  </Button>
                )}
                {onWriteDown && (
                  <Button
                    type="button"
                    onClick={() => onWriteDown(product)}
                    className="flex-1"
                    variant="secondary"
                  >
                    {t("products.actions.writeDown")}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </SlideOver>
  );
};

export default ProductDetailModal;
